import React, { useEffect, useRef, useState } from "react";

const EFFECTS = {
  fadeup: "opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300 ease-out",
  crystal: "effect-crystal",
};

const ScrollFadeUp = ({ delay = 0, className = "", children }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-[opacity,transform] duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
};

const ModalEnter = ({ className = "", children }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);
  return (
    <div
      className={`transition-[opacity,transform] duration-300 ease-out ${visible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-95"} ${className}`}
    >
      {children}
    </div>
  );
};

const LetterFadeUp = ({ delay = 0, className = "", children }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const text = typeof children === "string" ? children : "";
  const letters = text.split("");

  return (
    <span ref={ref} className={`inline-block ${className}`} aria-label={text}>
      {letters.map((ch, i) => (
        <span
          key={i}
          aria-hidden="true"
          className="inline-block"
          style={{
            animation: visible ? "letter-float 2.5s ease-in-out infinite" : "none",
            animationDelay: `${delay + i * 40 + 500}ms`,
          }}
        >
          <span
            className="inline-block transition-[opacity,transform] duration-500 ease-out"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(20px)",
              transitionDelay: `${delay + i * 40}ms`,
              whiteSpace: ch === " " ? "pre" : undefined,
            }}
          >
            {ch === " " ? " " : ch}
          </span>
        </span>
      ))}
    </span>
  );
};

const Effects = ({ type, delay = 0, className = "", children }) => {
  const types = Array.isArray(type) ? type : type.split(" ");

  if (types.includes("scroll-fadeup")) {
    return <ScrollFadeUp delay={delay} className={className}>{children}</ScrollFadeUp>;
  }

  if (types.includes("letter-fadeup")) {
    return <LetterFadeUp delay={delay} className={className}>{children}</LetterFadeUp>;
  }

  if (types.includes("modal-enter")) {
    return <ModalEnter className={className}>{children}</ModalEnter>;
  }

  const effectClass = types.map((t) => EFFECTS[t] ?? "").join(" ").trim();
  const combined = `${effectClass} ${className}`.trim();
  const style = delay ? { transitionDelay: `${delay}ms` } : undefined;

  if (React.isValidElement(children)) {
    return React.cloneElement(children, {
      className: `${children.props.className ?? ""} ${combined}`.trim(),
      style: { ...children.props.style, ...style },
    });
  }

  return (
    <div className={combined} style={style}>
      {children}
    </div>
  );
};

export default Effects;
